/**
 * The wire format of an unlock code, and the byte plumbing it needs.
 *
 * A code is three dot-separated parts:
 *
 *     NC1.<payload>.<signature>
 *
 * `payload` is base64url of a small ASCII JSON object; `signature` is base64url
 * of the 64 raw Ed25519 bytes over *the payload segment's own ASCII bytes* —
 * not over the JSON. Signing the encoded text rather than the decoded object
 * means verification never has to re-serialise anything, so a key ordering or
 * a space cannot change the answer.
 *
 * Everything here is written by hand rather than reached for: the engine has
 * no `atob`, no `Buffer` and no `TextEncoder` (its lib is ES2022 and nothing
 * else), and a code is ASCII by construction, so a byte is a character.
 */

/** What a valid code says. Nothing about the person — only that a sale happened. */
export interface License {
  /** Format version, so a future shape can be told apart from this one. */
  v: 1;
  /** Which till rang it up. */
  src: 'ios' | 'and' | 'web';
  /** Opaque 8-hex digest of the store's transaction id. Not reversible. */
  id: string;
  /** Original purchase, unix seconds. */
  t: number;
}

export const TOKEN_PREFIX = 'NC1';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Reverse lookup, built once: character code -> 6-bit value, or -1. */
const VALUES = (() => {
  const table = new Int8Array(128).fill(-1);
  for (let i = 0; i < ALPHABET.length; i += 1) table[ALPHABET.charCodeAt(i)] = i;
  return table;
})();

/** Bytes of an ASCII string. Any character above 0x7f makes the code invalid anyway. */
export function asciiBytes(text: string): Uint8Array {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) out[i] = text.charCodeAt(i) & 0x7f;
  return out;
}

/** base64url, no padding. */
export function toBase64url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += ALPHABET[a >> 2];
    out += ALPHABET[((a & 3) << 4) | ((b ?? 0) >> 4)];
    if (b === undefined) break;
    out += ALPHABET[((b & 15) << 2) | ((c ?? 0) >> 6)];
    if (c === undefined) break;
    out += ALPHABET[c & 63];
  }
  return out;
}

/** base64url back to bytes, or null if the text is not base64url at all. */
export function fromBase64url(text: string): Uint8Array | null {
  if (text.length % 4 === 1) return null;

  const out = new Uint8Array((text.length * 3) >> 2);
  let at = 0;
  let acc = 0;
  let bits = 0;

  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    const value = code < 128 ? VALUES[code] : -1;
    if (value < 0) return null;
    acc = (acc << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[at] = (acc >> bits) & 0xff;
      at += 1;
    }
  }
  return out.subarray(0, at);
}

/**
 * Whitespace, casing of the prefix and stray punctuation are the user's
 * problem right up until they paste; then they are ours. A code that survives
 * a round trip through a chat app should still work.
 */
export function normalizeToken(input: string): string {
  return input.replace(/\s+/g, '').replace(/^["'<]+|[">']+$/g, '');
}

/** Split a code into its parts without judging the signature. */
export function splitToken(input: string): { payload: string; signature: string } | null {
  const parts = normalizeToken(input).split('.');
  if (parts.length !== 3) return null;
  if (parts[0] !== TOKEN_PREFIX) return null;
  if (!parts[1] || !parts[2]) return null;
  return { payload: parts[1], signature: parts[2] };
}

/** Read the claims out of a payload segment. Says nothing about authenticity. */
export function decodePayload(payload: string): License | null {
  const bytes = fromBase64url(payload);
  if (!bytes) return null;

  let text = '';
  for (let i = 0; i < bytes.length; i += 1) text += String.fromCharCode(bytes[i]);

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;
  const claim = parsed as Record<string, unknown>;

  if (claim.v !== 1) return null;
  if (claim.src !== 'ios' && claim.src !== 'and' && claim.src !== 'web') return null;
  if (typeof claim.id !== 'string' || !/^[0-9a-f]{8}$/.test(claim.id)) return null;
  if (typeof claim.t !== 'number' || !Number.isFinite(claim.t)) return null;

  return { v: 1, src: claim.src, id: claim.id, t: claim.t };
}

/** Build the payload segment for a set of claims. Used by the signer and by tests. */
export function encodePayload(license: License): string {
  const json = `{"v":1,"src":"${license.src}","id":"${license.id}","t":${license.t}}`;
  return toBase64url(asciiBytes(json));
}

/** The short thing to print in a UI: `#a3f9c1`. Enough to identify, not to reuse. */
export function licenseLabel(license: License): string {
  return `#${license.id.slice(0, 6)}`;
}
